import React, { useState } from 'react';
import { X, MapPin, Send } from 'lucide-react';
import { siteVisitApi, actionApi, leadApi, taskApi } from '../services/api';
import { Lead, Task, SiteVisitTimeSlot, SiteVisitPickupOption } from '../types';
import { format } from 'date-fns';
import { istHour, istToday, shiftDay } from '../lib';

// Books THE site visit through the single path: POST /leads/{id}/site-visit.
// The backend sends the lead the WhatsApp confirmation and books the advisor's
// T-2 reminder call; re-posting with a new date/slot reschedules the open visit.
// Cancel/complete go through the nurture task verbs (task_service hooks handle
// the visit side effects).

interface SiteVisitModalProps {
  lead: Lead;
  visitTask: Task | null; // the open site_visit task, when one exists
  // An AI-proposed site_visit under review: pre-fills the form, shows the
  // evidence line and offers Reject. Booking adopts it server-side.
  proposal?: Task | null;
  onClose: () => void;
  onChanged: () => void;
}

// The slots the Nurture app offers. `early_morning` still exists on the wire,
// so a visit already booked at 7 AM keeps showing its slot.
const EARLY_MORNING = { value: 'early_morning' as SiteVisitTimeSlot, label: 'Early Morning (7 AM)', hour: 7 };
const TIME_SLOTS: Array<{ value: SiteVisitTimeSlot; label: string; hour: number }> = [
  { value: 'morning', label: 'Morning (10 AM)', hour: 10 },
  { value: 'afternoon', label: 'Afternoon (3 PM)', hour: 15 },
  { value: 'evening', label: 'Evening (5 PM)', hour: 17 },
];
const NAMED_SLOTS = [EARLY_MORNING, ...TIME_SLOTS];

// Today is bookable until this IST hour — the same rule as the Nurture app and
// the chat flow. A custom time rides `time_slot` as "HH:MM".
const TODAY_CUTOFF_HOUR = 17;
const isClockTime = (slot: string) => /^\d{2}:\d{2}$/.test(slot);
const clockLabel = (hhmm: string) => {
  const [h, m] = hhmm.split(':').map(Number);
  return `${((h + 11) % 12) + 1}:${String(m).padStart(2, '0')} ${h < 12 ? 'AM' : 'PM'}`;
};
const slotLabel = (slot: string) =>
  NAMED_SLOTS.find((x) => x.value === slot)?.label ?? (isClockTime(slot) ? clockLabel(slot) : slot);

const PICKUP_OPTIONS: Array<{ value: SiteVisitPickupOption; label: string }> = [
  { value: 'home_pickup', label: 'Home pickup' },
  { value: 'from_office', label: 'From our Kompally office' },
  { value: 'own_vehicle', label: 'Own vehicle' },
  { value: 'tbd', label: 'To be decided' },
];

/** The operator-facing assumptive-close script — the Nurture app's, verbatim. */
const SCRIPT_HINT =
  'Weekends fill up and our team must be free to host you properly — pencil in a slot now. ' +
  "They can change it anytime, and we'll call a day or two before anyway.";

/** Today (while before the cutoff), tomorrow + the next two weekends, deduped
 *  when they collide (a Friday's "Tomorrow" is "This Sat" — the first label
 *  wins). IST day keys. */
const quickDates = (todayOpen: boolean): { label: string; date: string }[] => {
  const today = istToday();
  const dow = new Date(`${today}T12:00:00Z`).getUTCDay();
  const thisSat = shiftDay(today, ((6 - dow + 7) % 7) || 7);
  const thisSun = shiftDay(today, ((7 - dow) % 7) || 7);
  const seen = new Set<string>();
  return [
    ...(todayOpen ? [{ label: 'Today', date: today }] : []),
    { label: 'Tomorrow', date: shiftDay(today, 1) },
    { label: 'This Sat', date: thisSat },
    { label: 'This Sun', date: thisSun },
    { label: 'Next Sat', date: shiftDay(thisSat, 7) },
    { label: 'Next Sun', date: shiftDay(thisSun, 7) },
  ].filter((c) => !seen.has(c.date) && !!seen.add(c.date));
};

const SiteVisitModal: React.FC<SiteVisitModalProps> = ({ lead, visitTask, proposal, onClose, onChanged }) => {
  const isEditing = !!visitTask;
  const payload = visitTask?.payload || proposal?.payload || {};
  const source = visitTask || proposal;

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showFeedbackForm, setShowFeedbackForm] = useState(false);
  const [feedbackNote, setFeedbackNote] = useState('');

  const [visitDate, setVisitDate] = useState<string>(
    payload.visit_date ||
    (source ? format(new Date(source.due_at), 'yyyy-MM-dd') : '')
  );
  const [timeSlot, setTimeSlot] = useState<SiteVisitTimeSlot>(payload.time_slot || 'morning');
  const [customTime, setCustomTime] = useState<boolean>(!!payload.time_slot && isClockTime(payload.time_slot));

  const nowHour = istHour();
  const todayOpen = nowHour < TODAY_CUTOFF_HOUR;
  const firstDay = todayOpen ? istToday() : shiftDay(istToday(), 1);
  const isToday = visitDate === istToday();
  // On a today booking only times still ahead are bookable.
  const slotOpen = (hour: number) => !isToday || hour > nowHour;
  const timeInPast = isToday && isClockTime(timeSlot) && Number(timeSlot.slice(0, 2)) <= nowHour;
  const namedHour = NAMED_SLOTS.find((x) => x.value === timeSlot)?.hour;
  const bookable = !!visitDate && !timeInPast &&
    (namedHour !== undefined ? slotOpen(namedHour) : isClockTime(timeSlot));
  const [pickupOption, setPickupOption] = useState<SiteVisitPickupOption>(payload.pickup_option || 'tbd');
  const [location, setLocation] = useState<string>(payload.location || '');

  const run = async (fn: () => Promise<void>) => {
    setLoading(true);
    setError(null);
    try {
      await fn();
      onChanged();
      onClose();
    } catch (err: any) {
      console.error('Site visit operation failed:', err);
      setError(err?.response?.data?.detail || err?.message || 'Something went wrong');
      setLoading(false);
    }
  };

  const handleBook = () =>
    run(async () => {
      await siteVisitApi.book(lead.id, {
        visit_date: visitDate,
        time_slot: timeSlot,
        pickup_option: pickupOption,
        location: pickupOption === 'home_pickup' && location.trim() ? location.trim() : undefined,
      });
    });

  const handleMarkAsConducted = () =>
    run(async () => {
      if (!visitTask) return;
      if (feedbackNote.trim()) {
        await leadApi.addNote(lead.id, `Site visit feedback: ${feedbackNote.trim()}`);
      }
      await actionApi.complete(visitTask.id);
    });

  const handleReject = () =>
    run(async () => {
      if (!proposal) return;
      await taskApi.reject(proposal.id);
    });

  const handleCancelVisit = () =>
    run(async () => {
      if (!visitTask) return;
      await actionApi.cancel(visitTask.id, 'Cancelled by operator');
    });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />

      {/* Modal */}
      <div className="relative bg-warm-white border border-sketch rounded-lg w-full max-w-lg max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header - Orange for site visits */}
        <div className="bg-gold p-4 flex items-center justify-between">
          <div className="flex items-center">
            <MapPin className="w-6 h-6 text-warm-white mr-3" strokeWidth={1.75} />
            <div>
              <h2 className="text-warm-white font-medium">
                {isEditing ? 'Site Visit' : 'Book Site Visit'}
              </h2>
              <p className="text-warm-white/70 text-sm">{lead.name}</p>
            </div>
          </div>
          <button onClick={onClose} className="text-warm-white hover:text-warm-white/80">
            <X className="w-6 h-6" strokeWidth={1.75} />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto p-6">
          {!showFeedbackForm ? (
            <div className="space-y-6">
              {proposal?.payload?.suggestion?.reason && (
                <div className="bg-gold/10 border border-gold/40 rounded p-3">
                  <span className="chip chip-gold mb-1">✦ AI suggested</span>
                  <p className="text-charcoal text-[13px] italic mt-1">“{proposal.payload.suggestion.reason}”</p>
                </div>
              )}
              {!proposal && !isEditing && (
                <div className="bg-sage/20 border-[1.5px] border-dashed border-forest/35 rounded-lg p-3 text-[12.5px] leading-snug">
                  {SCRIPT_HINT}
                </div>
              )}
              {/* Date */}
              <div>
                <label className="block text-xs text-graphite mb-1">Visit Date</label>
                <div className="flex gap-1.5 flex-wrap mb-2">
                  {quickDates(todayOpen).map((c) => (
                    <button key={c.date} type="button" className="pill" aria-pressed={visitDate === c.date} onClick={() => setVisitDate(c.date)}>
                      {c.label} <span className="opacity-70 ml-0.5">{format(new Date(`${c.date}T12:00:00`), 'd MMM')}</span>
                    </button>
                  ))}
                </div>
                <input
                  type="date"
                  value={visitDate}
                  min={firstDay}
                  onChange={(e) => setVisitDate(e.target.value)}
                  className="w-full px-3 py-2 bg-warm-white border border-sketch rounded text-charcoal text-sm focus:outline-none focus:border-forest/50"
                />
              </div>

              {/* Time slot */}
              <div>
                <label className="block text-xs text-graphite mb-2">Time</label>
                <div className="grid grid-cols-2 gap-2">
                  {(timeSlot === 'early_morning' ? NAMED_SLOTS : TIME_SLOTS).map((slot) => (
                    <button
                      key={slot.value}
                      disabled={!slotOpen(slot.hour)}
                      onClick={() => { setCustomTime(false); setTimeSlot(slot.value); }}
                      className={`px-3 py-2 rounded border text-sm transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${
                        timeSlot === slot.value
                          ? 'border-gold bg-gold/10 text-gold font-medium'
                          : 'border-sketch text-charcoal hover:border-sketch'
                      }`}
                    >
                      {slot.label}
                    </button>
                  ))}
                  <button
                    onClick={() => { setCustomTime(true); if (!isClockTime(timeSlot)) setTimeSlot('11:00'); }}
                    className={`px-3 py-2 rounded border text-sm transition-colors ${
                      customTime
                        ? 'border-gold bg-gold/10 text-gold font-medium'
                        : 'border-sketch text-charcoal hover:border-sketch'
                    }`}
                  >
                    {customTime ? clockLabel(timeSlot) : 'Custom time'}
                  </button>
                </div>
                {customTime && (
                  <input
                    type="time"
                    value={timeSlot}
                    min="06:00"
                    max="19:00"
                    step={300}
                    onChange={(e) => e.target.value && setTimeSlot(e.target.value)}
                    className="mt-2 w-full px-3 py-2 bg-warm-white border border-sketch rounded text-charcoal text-sm focus:outline-none focus:border-forest/50"
                  />
                )}
                {timeInPast && (
                  <p className="mt-1 text-xs text-brick">That time has already passed today — pick a later one.</p>
                )}
              </div>

              {/* Pickup */}
              <div>
                <label className="block text-xs text-graphite mb-2">Pickup</label>
                <div className="grid grid-cols-2 gap-2">
                  {PICKUP_OPTIONS.map((opt) => (
                    <button
                      key={opt.value}
                      onClick={() => setPickupOption(opt.value)}
                      className={`px-3 py-2 rounded border text-sm transition-colors ${
                        pickupOption === opt.value
                          ? 'border-forest bg-forest/10 text-forest font-medium'
                          : 'border-sketch text-charcoal hover:border-sketch'
                      }`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
                {pickupOption === 'home_pickup' && (
                  <input
                    type="text"
                    value={location}
                    onChange={(e) => setLocation(e.target.value)}
                    placeholder="Pickup location, e.g. Hitech City"
                    className="mt-2 w-full px-3 py-2 bg-warm-white border border-sketch rounded text-charcoal text-sm placeholder-graphite focus:outline-none focus:border-forest/50"
                  />
                )}
              </div>

              {/* The booking at a glance */}
              {visitDate && (
                <div className="bg-cream border-[1.5px] border-dashed border-sketch rounded-lg p-3 text-[13px] font-semibold">
                  {format(new Date(`${visitDate}T12:00:00`), 'EEE d MMM')}
                  {' · '}{slotLabel(timeSlot)}
                  {' · '}{pickupOption === 'home_pickup'
                    ? `home pickup${location.trim() ? ` from ${location.trim()}` : ''}`
                    : PICKUP_OPTIONS.find((x) => x.value === pickupOption)?.label.toLowerCase()}
                </div>
              )}

              {/* What booking does */}
              <div className="flex items-start bg-cream border border-sketch rounded p-3">
                <Send className="w-4 h-4 text-forest mr-2 mt-0.5 shrink-0" strokeWidth={1.75} />
                <p className="text-graphite text-xs">
                  {isEditing
                    ? 'Saving with a new date or slot reschedules the visit: the lead gets an updated WhatsApp confirmation and the reminder call moves with it.'
                    : 'Booking sends the lead a WhatsApp confirmation on the official channel and schedules the advisor’s reminder call two days before the visit.'}
                </p>
              </div>
            </div>
          ) : (
            // Conducted → optional feedback, saved as a lead note
            <div className="space-y-4">
              <h3 className="text-forest text-sm font-medium">Visit Feedback</h3>
              <textarea
                value={feedbackNote}
                onChange={(e) => setFeedbackNote(e.target.value)}
                placeholder="How did the visit go? Interest level, what they liked, objections, next steps... (optional — saved as a lead note)"
                className="w-full h-32 px-3 py-2 bg-warm-white border border-sketch rounded text-charcoal text-sm placeholder-graphite focus:outline-none focus:border-forest/50 resize-none"
              />
            </div>
          )}

          {error && (
            <p className="mt-4 text-sm text-brick">{error}</p>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-sketch flex justify-between">
          {isEditing && !showFeedbackForm ? (
            <>
              <button
                onClick={handleCancelVisit}
                disabled={loading}
                className="px-4 py-2 text-brick border border-brick/30 rounded hover:bg-brick/10 transition-colors"
              >
                Cancel Visit
              </button>
              <div className="flex gap-2">
                <button
                  onClick={handleBook}
                  disabled={loading || !bookable}
                  className="px-4 py-2 border border-forest/30 text-forest rounded hover:bg-forest/10 transition-colors disabled:opacity-50"
                >
                  {loading ? 'Saving...' : 'Reschedule'}
                </button>
                <button
                  onClick={() => setShowFeedbackForm(true)}
                  disabled={loading}
                  className="px-4 py-2 bg-gold text-warm-white rounded hover:bg-gold/80 transition-colors"
                >
                  Mark as Conducted
                </button>
              </div>
            </>
          ) : showFeedbackForm ? (
            <>
              <button
                onClick={() => setShowFeedbackForm(false)}
                className="px-4 py-2 text-graphite border border-sketch rounded hover:bg-cream transition-colors"
              >
                Back
              </button>
              <button
                onClick={handleMarkAsConducted}
                disabled={loading}
                className="px-4 py-2 bg-forest text-warm-white rounded hover:bg-forest/90 transition-colors"
              >
                {loading ? 'Saving...' : 'Complete Visit'}
              </button>
            </>
          ) : (
            <>
              <button
                onClick={proposal ? handleReject : onClose}
                disabled={loading}
                className="px-4 py-2 text-graphite border border-sketch rounded hover:bg-cream transition-colors"
              >
                {proposal ? '✕ Reject suggestion' : 'Cancel'}
              </button>
              <button
                onClick={handleBook}
                disabled={loading || !bookable}
                className="px-4 py-2 bg-gold text-warm-white rounded hover:bg-gold/80 transition-colors disabled:opacity-50"
              >
                {loading ? 'Booking...' : 'Book & Send Confirmation'}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default SiteVisitModal;
